/**
 * Composition pour la gestion d'un formulaire
 */

import { ref, computed } from 'vue';
import axios from '@js/tools/axios';
/***/
import DomElement from '@classes/DomElement';

export function useForm({
    requestPath,
    requestMethod = 'post',
    onSuccess
}) {

    const requesting = ref(false);
    const formRef = ref(null); // Référence du DOM sur la balise form
    const genericErrorRef = ref(null);  // Référence du DOM sur l'erreur générique p.generic-message-error
    const withGenericError = ref(null); // Vrai s'il y a au moins une erreur
    const errors = ref({});

    // Retourne l'URL
    const getRequestPath = (() => {
        if(typeof requestPath === 'function') {
            return requestPath.call();
        }
        return requestPath.value || requestPath;
    });

    // Soumission du formulaire
    const submit = ((event) => {
        if(requesting.value) {
            return;
        }

        requesting.value = true;

        errors.value = {};
        withGenericError.value = null;

        let axiosParams;
        const formData = new FormData(event.target);
        // formData.set('_method', requestMethod)

        if(requestMethod === 'get') {
            axiosParams = {
                params: Object.fromEntries(formData)
            };
        } else {
            axiosParams = formData;
        }

        return axios[requestMethod](getRequestPath(), axiosParams, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        }).then((response) => {

            let data = null;
            if(response && response.data)
            {
                data = response.data;
            }

            if(data && data.success) {
                onSuccess.call(null, data);
            } else {
                withGenericError.value = true;
                return Promise.reject(data);
            }
        }).catch(function(error) {
            withGenericError.value = true;
            if(error && error.response && error.response.data && error.response.data.errors) {
                errors.value = error.response.data.errors;
            }

            setTimeout(() => {
                if(genericErrorRef.value) {
                    genericErrorRef.value.scrollIntoView({ behavior: "smooth" });
                }
            }, 300);

            return Promise.reject(error);
        }).finally(() => {
            requesting.value = false;
        });
    });

    return {
        requesting,
        formRef,
        genericErrorRef,
        withGenericError,
        errors,
        submit
    };

};

/**
 * Composition pour les formulaires ayant un menu par onglet
 * @param string | Proxy | callable requestPath
 * @param string requestMethod
 * @param callable onSuccess
 * @param string defaultTabMenu
 * @return object
 */
export function useFormWithTabMenu({
    requestPath,
    requestMethod = 'post',
    onSuccess,
    defaultTabMenu
}) {

    const currentMenuTab = ref(defaultTabMenu);

    const {
        requesting,
        formRef,
        genericErrorRef,
        withGenericError,
        errors,
        submit
    } = useForm({
        requestPath,
        requestMethod,
        onSuccess
    });

    // Retourne l'onglet à afficher en cas d'erreur
    // On recherche la première erreur, puis la référence à l'onglet (data-tab) dans un des parents
    const getTabIfError = (() => {
        if(formRef.value === null) {
            return null;
        }

        const firstFormGroupError = formRef.value.querySelector('div.form-group-error');
        if(firstFormGroupError === null) {
            return null;
        }

        const formGroupParent = DomElement.getParent(firstFormGroupError, '[data-tab]');
        if(formGroupParent === null) {
            return null;
        }

        return (formGroupParent.dataset.tab ?? null);
    });

    // Requête de soumission du formulaire
    const requestSubmit = ((event) => {

        return submit(event).catch((response) => {

            // Affichage de l'onglet où apparaît la première erreur
            const newTab = getTabIfError();
            if(newTab !== null) {
                currentMenuTab.value = newTab;
            }

            return Promise.reject(response);

        });

    });

    return {
        requesting,
        formRef,
        genericErrorRef,
        withGenericError,
        errors,
        submit: requestSubmit,
        currentMenuTab
    };

}
